import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService, PrimeTemplate} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {finalize, timer} from 'rxjs';
import {distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';

import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {UserLinkComponent} from '../../shared/components/user-link/user-link.component';
import {lastSyncStatusToTag, loginStatusToTag, nycProfileStatusToTag, Severity} from '../../shared/enum-to-tag';
import {DATE_TIME_FORMAT} from '../../shared/formats';
import {shallowEqual, updateQueryParams} from '../../shared/query-params';
import {userDisplayName} from '../../shared/user-display-name';
import {silentErrors} from '../../shared/http-context';

import {UserSyncClient, UsersClient} from '../../api-client/api/api';
import {CircuitBreakerStatusDto} from '../../api-client/model/circuit-breaker-status-dto';
import {UserRegistrationDto} from '../../api-client/model/user-registration-dto';

type FilterValue = 'all' | 'to-register' | 'sync-failed';

type FilterOption = { label: string; value: FilterValue };

const filterOptions: FilterOption[] = [
  {label: 'All', value: 'all'},
  {label: 'To register', value: 'to-register'},
  {label: 'Sync failed', value: 'sync-failed'},
];

type AutoCreateStatusTag = { value: string; severity: Severity };

type CircuitBreakerState = 'tripped' | 'ok' | 'unknown';

@Component({
  selector: 'homey-nyc-sync-page',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    UserLinkComponent,
    InputText,
    Select,
  ],
  templateUrl: './nyc-sync-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NycSyncPageComponent {
  private readonly userSyncClient = inject(UserSyncClient);
  private readonly usersClient = inject(UsersClient);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly users = signal<UserRegistrationDto[]>([]);
  protected readonly totalCount = signal(0);
  // The index reports a window rather than a total when a query is too broad to enumerate.
  protected readonly truncated = signal(false);

  /**
   * Both controls live in the query string so that the queue -> user -> back round trip keeps the
   * operator's place. The router writes `undefined` for an absent param regardless of the declared
   * default, hence the `??` fallbacks below.
   */
  protected readonly search = input('');
  protected readonly filter = input<FilterValue>('to-register');
  protected readonly selectedFilter = computed(() => this.filter() ?? 'to-register');
  protected readonly filterOptions = filterOptions;

  private readonly searchParams = computed(() => ({
    search: this.search() ?? '',
    view: this.selectedFilter(),
  }));

  protected readonly dateTimeFormat = DATE_TIME_FORMAT;
  protected readonly userDisplayName = userDisplayName;

  protected readonly nycProfileStatusToTag = nycProfileStatusToTag;
  protected readonly lastSyncStatusToTag = lastSyncStatusToTag;
  protected readonly loginStatusToTag = loginStatusToTag;

  protected readonly circuitBreakerStatus = signal<CircuitBreakerStatusDto | undefined>(undefined);
  protected readonly circuitBreakerLoading = signal(true);
  protected readonly circuitBreakerState = computed<CircuitBreakerState>(() => {
    const status = this.circuitBreakerStatus();
    if (status === undefined) {
      return 'unknown';
    }
    return status.trippedByErrors || status.trippedBySlowCalls ? 'tripped' : 'ok';
  });
  protected readonly circuitBreakerDetail = computed(() => {
    const status = this.circuitBreakerStatus();
    if (status === undefined) {
      return '— status could not be loaded';
    }
    return `· ${status.errorCalls} errors, ${status.slowCalls} slow of ${status.totalCalls} calls in the last ${status.windowMinutes} min`;
  });

  constructor() {
    toObservable(this.searchParams)
      .pipe(
        // switchMap+timer rather than debounceTime: index 0 is the initial load and goes straight
        // through, so arriving on this page does not cost 300ms of dead time before the request
        // even starts. Later emissions (typing) still debounce, because switchMap cancels the
        // pending timer when a new one arrives.
        switchMap((params, index) => timer(index === 0 ? 0 : 300).pipe(map(() => params))),
        distinctUntilChanged(shallowEqual),
        switchMap(params =>
          this.usersClient.searchRegistrations(
            params.search || undefined,
            params.view === 'all' ? undefined : params.view
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(result => {
        this.users.set(result.users);
        this.totalCount.set(result.totalCount);
        this.truncated.set(result.truncated);
      });

    this.loadCircuitBreakerStatus();
  }

  protected onSearchChange(value: string) {
    updateQueryParams(this.router, this.route, {search: value || null});
  }

  protected onFilterChange(value: FilterValue) {
    updateQueryParams(this.router, this.route, {filter: value});
  }

  private loadCircuitBreakerStatus() {
    this.circuitBreakerLoading.set(true);
    this.userSyncClient
      // silentErrors: the catch block below reports this failure in the panel's own words, so the
      // interceptor's generic toast would just stack a second one on top.
      .getCircuitBreakerStatus('body', false, {context: silentErrors()})
      .pipe(
        finalize(() => this.circuitBreakerLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: status => this.circuitBreakerStatus.set(status),
        error: () => {
          // Drop any previous reading: a failed refresh must not keep presenting a stale value as current.
          this.circuitBreakerStatus.set(undefined);
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to load NYC sync status',
          });
        },
      });
  }

  protected autoCreateStatusTag(user: UserRegistrationDto): AutoCreateStatusTag {
    switch (user.autoCreateStatus) {
      case 'in-progress':
        return {value: 'In Progress', severity: 'warn'};
      case 'failed':
        return {value: 'Failed', severity: 'danger'};
      case 'pending':
        return {value: 'Pending', severity: 'secondary'};
    }
  }
}
